import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  deriveXvtDashboard,
  type XvtDecision,
  type XvtShipmentRaw,
} from '../data/xvtShipment';
import './featurePage.css';
import './XvtShipmentPage.css';

const TARGET = 70;

/* ------------------------------------------------------------------ */
/*  Data fetching                                                      */
/* ------------------------------------------------------------------ */

type LoadState = 'loading' | 'ready' | 'error';

/** Fetch the raw XVT shipment assessments from the backend API. */
function useXvtShipment() {
  const [raw, setRaw] = useState<XvtShipmentRaw | null>(null);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch('/api/dashboard/xvt-shipment', {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as XvtShipmentRaw;
        if (!cancelled) {
          setRaw(data);
          setState('ready');
        }
      } catch {
        if (!cancelled) setState('error');
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { raw, state };
}

/* ------------------------------------------------------------------ */
/*  Donut chart (SVG)                                                  */
/* ------------------------------------------------------------------ */

interface Segment {
  name: string;
  value: number;
  color: string;
}

function polar(cx: number, cy: number, r: number, angle: number) {
  return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
}

function DonutChart({ segments }: { segments: Segment[] }) {
  const cx = 90;
  const cy = 90;
  const R = 78;
  const r = 46;
  const total = segments.reduce((s, d) => s + d.value, 0) || 1;
  const active = segments.filter((s) => s.value > 0);

  let angle = -Math.PI / 2;
  const arcs = active.map((seg) => {
    const frac = seg.value / total;
    const a0 = angle;
    const a1 = angle + frac * 2 * Math.PI;
    angle = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const o0 = polar(cx, cy, R, a0);
    const o1 = polar(cx, cy, R, a1);
    const i1 = polar(cx, cy, r, a1);
    const i0 = polar(cx, cy, r, a0);
    const d = `M${o0.x} ${o0.y} A${R} ${R} 0 ${large} 1 ${o1.x} ${o1.y} L${i1.x} ${i1.y} A${r} ${r} 0 ${large} 0 ${i0.x} ${i0.y} Z`;
    return { d, seg, frac };
  });

  const single = active.length === 1;

  return (
    <div className="xvt-donut">
      <svg viewBox="0 0 180 180" className="xvt-donut__svg" role="img" aria-label="Decision breakdown">
        {single ? (
          <circle cx={cx} cy={cy} r={(R + r) / 2} fill="none" stroke={active[0].color} strokeWidth={R - r} />
        ) : (
          arcs.map((a, i) => <path key={i} d={a.d} fill={a.seg.color} />)
        )}
        <text x={cx} y={cy - 4} textAnchor="middle" className="xvt-donut__num">
          {total}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" className="xvt-donut__cap">
          total
        </text>
      </svg>
      <ul className="xvt-donut__legend">
        {segments.map((s) => (
          <li key={s.name}>
            <span className="xvt-donut__swatch" style={{ background: s.color }} />
            <span className="xvt-donut__legend-name">{s.name}</span>
            <span className="xvt-donut__legend-val">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Timeline area chart (SVG)                                          */
/* ------------------------------------------------------------------ */

interface TimePoint {
  label: string;
  rate: number;
}

function Timeline({ points }: { points: TimePoint[] }) {
  const W = 460;
  const H = 200;
  const padL = 34;
  const padR = 14;
  const padT = 16;
  const padB = 28;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const x = (i: number) =>
    padL + (points.length === 1 ? plotW / 2 : (plotW / (points.length - 1)) * i);
  const y = (rate: number) => padT + plotH * (1 - rate / 100);

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)} ${y(p.rate)}`).join(' ');
  const areaPath = `${linePath} L${x(points.length - 1)} ${padT + plotH} L${x(0)} ${padT + plotH} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="xvt-timeline" role="img" aria-label="Cumulative pass rate over time">
      <defs>
        <linearGradient id="xvtArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#16a34a" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#16a34a" stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* gridlines + y labels */}
      {[0, 50, 100].map((v) => (
        <g key={v}>
          <line x1={padL} y1={y(v)} x2={W - padR} y2={y(v)} className="xvt-timeline__grid" />
          <text x={padL - 6} y={y(v) + 3} textAnchor="end" className="xvt-timeline__axis">
            {v}%
          </text>
        </g>
      ))}

      {/* target line */}
      <line x1={padL} y1={y(TARGET)} x2={W - padR} y2={y(TARGET)} className="xvt-timeline__target" />
      <text x={W - padR} y={y(TARGET) - 4} textAnchor="end" className="xvt-timeline__target-label">
        Target {TARGET}%
      </text>

      <path d={areaPath} fill="url(#xvtArea)" />
      <path d={linePath} className="xvt-timeline__line" />

      {points.map((p, i) => (
        <g key={p.label}>
          <circle cx={x(i)} cy={y(p.rate)} r="3.6" className="xvt-timeline__dot" />
          <text x={x(i)} y={y(p.rate) - 8} textAnchor="middle" className="xvt-timeline__value">
            {p.rate}%
          </text>
          <text x={x(i)} y={H - 9} textAnchor="middle" className="xvt-timeline__axis">
            {p.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Decision badge                                                     */
/* ------------------------------------------------------------------ */

function DecisionBadge({ decision, qualifier }: { decision: XvtDecision; qualifier: string | null }) {
  if (decision === 'APPROVED') {
    if (qualifier === 'Risk Advisory')
      return <span className="xvt-badge xvt-badge--risk">Approved · Risk</span>;
    if (qualifier === 'Waiver Applied')
      return <span className="xvt-badge xvt-badge--waiver">Approved · Waiver</span>;
    return <span className="xvt-badge xvt-badge--pass">Approved</span>;
  }
  if (qualifier === 'Missing Data')
    return <span className="xvt-badge xvt-badge--missing">Rejected · No Data</span>;
  return <span className="xvt-badge xvt-badge--fail">Rejected</span>;
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const PROJ_H = 150;

export function XvtShipmentPage() {
  const { raw, state } = useXvtShipment();

  // Derive every aggregation from the raw records (source of truth).
  const dashboard = useMemo(() => (raw ? deriveXvtDashboard(raw) : null), [raw]);
  const assessments = useMemo(() => dashboard?.assessments ?? [], [dashboard]);

  const donutSegments = useMemo<Segment[]>(() => {
    const clean = assessments.filter((a) => a.decision === 'APPROVED' && !a.qualifier).length;
    const risk = assessments.filter((a) => a.decision === 'APPROVED' && a.qualifier).length;
    const rejected = assessments.filter((a) => a.decision === 'REJECTED').length;
    return [
      { name: 'Approved', value: clean, color: '#16a34a' },
      { name: 'Approved (Risk/Waiver)', value: risk, color: '#f59e0b' },
      { name: 'Rejected', value: rejected, color: '#ef4444' },
    ];
  }, [assessments]);

  const supplierRows = useMemo(
    () => Object.entries(dashboard?.suppliers ?? {}).sort((a, b) => b[1].passRate - a[1].passRate),
    [dashboard],
  );

  const projectRows = useMemo(() => Object.entries(dashboard?.projects ?? {}), [dashboard]);
  const projMax = Math.max(...projectRows.map(([, p]) => p.total), 1);

  const timelinePoints = useMemo<TimePoint[]>(() => {
    const dates = [...new Set(assessments.map((a) => a.date))].sort();
    let cumApproved = 0;
    let cumTotal = 0;
    return dates.map((d) => {
      const day = assessments.filter((a) => a.date === d);
      cumTotal += day.length;
      cumApproved += day.filter((a) => a.decision === 'APPROVED').length;
      return { label: d.slice(5), rate: Math.round((cumApproved / cumTotal) * 100) };
    });
  }, [assessments]);

  if (state === 'loading') {
    return (
      <div className="page xvt">
        <div className="xvt-state">
          <span className="xvt-state__spinner" aria-hidden />
          <p>Loading shipment approval data…</p>
        </div>
      </div>
    );
  }

  if (state === 'error' || !dashboard) {
    return (
      <div className="page xvt">
        <Link to="/portal/sqm-consultant" className="xvt-back">
          ← Back to Readiness Review
        </Link>
        <div className="xvt-state xvt-state--error">
          <p>Unable to load shipment approval data right now. Please refresh to try again.</p>
        </div>
      </div>
    );
  }

  const { summary, suppliers } = dashboard;
  const updated = new Date(dashboard.lastUpdated).toLocaleString();

  return (
    <div className="page xvt">
      {/* Back link */}
      <Link to="/portal/sqm-consultant" className="xvt-back">
        ← Back to Readiness Review
      </Link>

      {/* Header */}
      <div className="xvt-header">
        <div className="xvt-header__lead">
          <span className="xvt-header__eyebrow">Readiness Review</span>
          <h1 className="xvt-header__title">XVT Shipment Approval</h1>
        </div>
        <div className="xvt-header__meta">
          <div className="xvt-header__live">
            <span className="xvt-header__dot" /> Live Data
          </div>
          <div>Updated {updated}</div>
        </div>
      </div>

      {/* KPIs */}
      <div className="xvt-kpis">
        <div className="xvt-kpi">
          <div className="xvt-kpi__label">Total Assessments</div>
          <div className="xvt-kpi__value xvt-kpi__value--primary">{summary.totalAssessments}</div>
          <div className="xvt-kpi__sub">{summary.projectsCount} projects</div>
        </div>
        <div className="xvt-kpi">
          <div className="xvt-kpi__label">Pass Rate</div>
          <div className="xvt-kpi__value xvt-kpi__value--success">{summary.passRate}%</div>
          <div className="xvt-kpi__sub">
            {summary.approved} passed / {summary.rejected} failed
          </div>
        </div>
        <div className="xvt-kpi">
          <div className="xvt-kpi__label">Approved</div>
          <div className="xvt-kpi__value xvt-kpi__value--success">{summary.approved}</div>
          <div className="xvt-kpi__sub">shipments cleared</div>
        </div>
        <div className="xvt-kpi">
          <div className="xvt-kpi__label">Rejected</div>
          <div className="xvt-kpi__value xvt-kpi__value--error">{summary.rejected}</div>
          <div className="xvt-kpi__sub">shipments blocked</div>
        </div>
        <div className="xvt-kpi">
          <div className="xvt-kpi__label">Suppliers</div>
          <div className="xvt-kpi__value">{summary.suppliersCount}</div>
          <div className="xvt-kpi__sub">{Object.keys(suppliers).join(', ')}</div>
        </div>
      </div>

      {/* Charts row 1 */}
      <div className="xvt-grid-2">
        <div className="xvt-card">
          <h3 className="xvt-card__title">Decision Breakdown</h3>
          <DonutChart segments={donutSegments} />
        </div>
        <div className="xvt-card">
          <h3 className="xvt-card__title">Supplier Pass Rate</h3>
          <div className="xvt-bars">
            {supplierRows.map(([name, s]) => {
              const ok = s.passRate >= TARGET;
              return (
                <div className="xvt-bar" key={name}>
                  <span className="xvt-bar__name">{name}</span>
                  <span className="xvt-bar__track">
                    <span
                      className={`xvt-bar__fill${ok ? '' : ' xvt-bar__fill--low'}`}
                      style={{ width: `${s.passRate}%` }}
                    />
                    <span className="xvt-bar__target" style={{ left: `${TARGET}%` }} />
                  </span>
                  <span className={`xvt-bar__val${ok ? '' : ' xvt-bar__val--low'}`}>
                    {s.passRate}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Charts row 2 */}
      <div className="xvt-grid-2">
        <div className="xvt-card">
          <h3 className="xvt-card__title">By Project</h3>
          <div className="xvt-proj" style={{ height: `${PROJ_H + 40}px` }}>
            {projectRows.map(([name, p]) => (
              <div className="xvt-proj__item" key={name}>
                <div className="xvt-proj__bar" style={{ height: `${PROJ_H}px` }}>
                  {p.rejected > 0 && (
                    <div
                      className="xvt-proj__seg xvt-proj__seg--rej"
                      style={{ height: `${(p.rejected / projMax) * PROJ_H}px` }}
                    >
                      {p.rejected}
                    </div>
                  )}
                  {p.approved > 0 && (
                    <div
                      className="xvt-proj__seg xvt-proj__seg--app"
                      style={{ height: `${(p.approved / projMax) * PROJ_H}px` }}
                    >
                      {p.approved}
                    </div>
                  )}
                </div>
                <div className="xvt-proj__label">
                  {name}
                  <span className="xvt-proj__rate">{p.passRate}%</span>
                </div>
              </div>
            ))}
          </div>
          <div className="xvt-proj__legend">
            <span>
              <i className="xvt-dot xvt-dot--app" /> Approved
            </span>
            <span>
              <i className="xvt-dot xvt-dot--rej" /> Rejected
            </span>
          </div>
        </div>
        <div className="xvt-card">
          <h3 className="xvt-card__title">Cumulative Pass Rate Over Time</h3>
          <Timeline points={timelinePoints} />
        </div>
      </div>

      {/* Tables */}
      <div className="xvt-grid-3">
        <div className="xvt-card">
          <h3 className="xvt-card__title">Assessment Log</h3>
          <div className="xvt-table-wrap">
            <table className="xvt-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Supplier</th>
                  <th>Project</th>
                  <th>Part(s)</th>
                  <th>Meas.</th>
                  <th>OOS</th>
                  <th>Decision</th>
                </tr>
              </thead>
              <tbody>
                {assessments
                  .slice()
                  .reverse()
                  .map((a) => (
                    <tr key={a.id}>
                      <td>{a.id}</td>
                      <td>{a.date}</td>
                      <td>{a.supplier}</td>
                      <td>{a.project}</td>
                      <td className="xvt-table__desc">{a.description}</td>
                      <td>{a.measurements}</td>
                      <td className={a.oos > 0 ? 'xvt-table__oos' : ''}>{a.oos}</td>
                      <td>
                        <DecisionBadge decision={a.decision} qualifier={a.qualifier} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="xvt-card">
          <h3 className="xvt-card__title">Supplier Scorecard</h3>
          <div className="xvt-table-wrap">
            <table className="xvt-table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>Assessed</th>
                  <th>Pass Rate</th>
                </tr>
              </thead>
              <tbody>
                {supplierRows.map(([name, s]) => {
                  const ok = s.passRate >= TARGET;
                  return (
                    <tr key={name}>
                      <td className="xvt-table__strong">{name}</td>
                      <td>{s.total}</td>
                      <td>
                        <span className="xvt-rate">
                          <span className="xvt-rate__track">
                            <span
                              className={`xvt-rate__fill${ok ? '' : ' xvt-rate__fill--low'}`}
                              style={{ width: `${s.passRate}%` }}
                            />
                          </span>
                          <span className={`xvt-rate__label${ok ? '' : ' xvt-rate__label--low'}`}>
                            {s.passRate}%
                          </span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="xvt-footer">
        XVT Shipment Approval System • Powered by DAP-AI • Data refreshes on each assessment
      </div>
    </div>
  );
}
